import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import ConfirmModal from './ConfirmModal';

const PaymentSchedule = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showProcessingModal, setShowProcessingModal] = useState(false);
  const [showCompletedModal, setShowCompletedModal] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const [uploadingProofId, setUploadingProofId] = useState(null);
  const [confirmModal, setConfirmModal] = useState({ show: false });
  const [emailing, setEmailing] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [recipients, setRecipients] = useState({ to: [], cc: [] });
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [reconResult, setReconResult] = useState(null);

  // Hidden file input ref for proof uploads
  const proofInputRef = useRef(null);
  const [activeUploadRecordId, setActiveUploadRecordId] = useState(null);

  useEffect(() => {
    fetchSchedule();
  }, [id]);

  const fetchSchedule = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/batches/${id}/schedule/`);
      setSchedule(response.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load payment schedule');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Determine if we're in send mode (APPROVED) or view-only mode
  const isSendMode = schedule?.batch?.status === 'APPROVED';

  const handlePreviewEmail = async () => {
    if (isSendMode) {
      try {
        setLoadingRecipients(true);
        setError(null);
        const response = await api.get('/api/accountant-recipients/?active_only=true');
        const results = response.data.results || response.data;
        
        const toList = results.filter(r => r.recipient_type === 'TO');
        const ccList = results.filter(r => r.recipient_type === 'CC');
        
        if (toList.length === 0) {
          setError('No active TO recipients configured. Please add at least one accountant email address before sending.');
          return;
        }
        
        setRecipients({ to: toList, cc: ccList });
        setShowPreviewModal(true);
      } catch (err) {
        setError('Failed to load recipients');
        console.error(err);
      } finally {
        setLoadingRecipients(false);
      }
    } else {
      setShowPreviewModal(true);
    }
  };

  const handleConfirmSend = async () => {
    try {
      setEmailing(true);
      setEmailSuccess(null);
      setError(null);
      
      const response = await api.post(`/api/batches/${id}/email-schedule/`);
      
      setShowPreviewModal(false);
      setEmailSuccess({
        message: 'Payment schedule emailed successfully',
        to: response.data.to_recipients,
        cc: response.data.cc_recipients,
        filename: response.data.filename,
      });

      if (schedule.batch.status === 'APPROVED') {
        setShowProcessingModal(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to email payment schedule');
      setShowPreviewModal(false);
    } finally {
      setEmailing(false);
    }
  };

  const handleStartProcessing = async () => {
    try {
      setUpdatingStatus(true);
      await api.post(`/api/batches/${id}/start-processing/`);
      await fetchSchedule();
      setShowProcessingModal(false);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update status');
      console.error(err);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleMarkPaid = async (recordId) => {
    try {
      setMarkingPaidId(recordId);
      const response = await api.post(`/api/batches/${id}/schedule/${recordId}/mark-paid/`);
      
      setSchedule(prev => ({
        ...prev,
        batch: {
          ...prev.batch,
          status: response.data.batch_completed ? 'PAYMENT_COMPLETED' : prev.batch.status,
        },
        paid_count: response.data.paid_count,
        local_payments: prev.local_payments.map(p =>
          p.id === recordId
            ? { ...p, is_paid: true, paid_at: response.data.paid_at, paid_by: response.data.paid_by }
            : p
        ),
        offshore_payments: prev.offshore_payments.map(p =>
          p.id === recordId
            ? { ...p, is_paid: true, paid_at: response.data.paid_at, paid_by: response.data.paid_by }
            : p
        ),
      }));

      if (response.data.batch_completed) {
        setShowCompletedModal(true);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to mark as paid');
      console.error(err);
    } finally {
      setMarkingPaidId(null);
    }
  };

  const handleUploadProofClick = (recordId) => {
    setActiveUploadRecordId(recordId);
    if (proofInputRef.current) {
      proofInputRef.current.value = '';
    }
    proofInputRef.current.click();
  };

  const handleProofFileSelected = async (e) => {
    const file = e.target.files[0];
    if (!file || !activeUploadRecordId) return;

    try {
      setUploadingProofId(activeUploadRecordId);
      
      const formData = new FormData();
      formData.append('proof_of_payment', file);

      const response = await api.post(
        `/api/batches/${id}/schedule/${activeUploadRecordId}/upload-proof/`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      const updatedProof = {
        has_proof: true,
        proof_filename: response.data.filename.split('/').pop(),
        proof_url: response.data.proof_url + '?t=' + Date.now(),
        proof_uploaded_at: response.data.uploaded_at,
        proof_uploaded_by: response.data.uploaded_by,
        recon_status: response.data.recon_status,
        recon_details: response.data.recon_details,
      };

      setSchedule(prev => ({
        ...prev,
        local_payments: prev.local_payments.map(p =>
          p.id === activeUploadRecordId ? { ...p, ...updatedProof } : p
        ),
        offshore_payments: prev.offshore_payments.map(p =>
          p.id === activeUploadRecordId ? { ...p, ...updatedProof } : p
        ),
      }));

      // Show recon results modal if we got recon data back
      if (response.data.recon_status && response.data.recon_status !== 'PENDING') {
        const payment = [...(schedule.local_payments || []), ...(schedule.offshore_payments || [])]
          .find(p => p.id === activeUploadRecordId);
        setReconResult({
          supplierName: payment?.supplier_name || 'Unknown',
          status: response.data.recon_status,
          details: response.data.recon_details,
        });
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload POP');
      console.error(err);
    } finally {
      setUploadingProofId(null);
      setActiveUploadRecordId(null);
    }
  };

  const handleDeleteProof = (recordId) => {
    setConfirmModal({
      show: true,
      title: 'Remove Proof of Payment',
      message: 'Are you sure you want to remove this proof of payment? This cannot be undone.',
      confirmText: 'Remove',
      confirmStyle: 'btn-danger',
      loading: false,
      onConfirm: async () => {
        try {
          setConfirmModal(prev => ({ ...prev, loading: true }));
          await api.delete(`/api/batches/${id}/schedule/${recordId}/delete-proof/`);

          const clearedProof = {
            has_proof: false,
            proof_filename: null,
            proof_url: null,
            proof_uploaded_at: null,
            proof_uploaded_by: null,
            recon_status: 'PENDING',
            recon_details: null,
          };

          setSchedule(prev => ({
            ...prev,
            local_payments: prev.local_payments.map(p =>
              p.id === recordId ? { ...p, ...clearedProof } : p
            ),
            offshore_payments: prev.offshore_payments.map(p =>
              p.id === recordId ? { ...p, ...clearedProof } : p
            ),
          }));
          setConfirmModal({ show: false });
        } catch (err) {
          setError(err.response?.data?.error || 'Failed to remove POP');
          console.error(err);
          setConfirmModal({ show: false });
        }
      },
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString();
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatAmount = (amount) => {
    if (!amount) return '-';
    return parseFloat(amount).toLocaleString('en-ZA', { minimumFractionDigits: 2 });
  };

  const renderTotalAmount = () => {
    const localTotal = parseFloat(schedule.local_total) || 0;
    const offshoreTotals = schedule.offshore_totals || [];

    if (localTotal === 0 && offshoreTotals.length === 0) return '-';

    return (
      <div className="currency-breakdown-inline">
        {localTotal > 0 && (
          <div className="breakdown-row">
            <span className="breakdown-currency-tag">ZAR</span>
            <span>{formatAmount(localTotal)}</span>
          </div>
        )}
        {offshoreTotals.map((item, index) => (
          <div key={index} className="breakdown-row">
            <span className="breakdown-currency-tag">{item.currency}</span>
            <span>{formatAmount(item.total)}</span>
          </div>
        ))}
      </div>
    );
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      APPROVED: { backgroundColor: '#10b981', color: 'white' },
      PAYMENT_PROCESSING: { backgroundColor: '#3b82f6', color: 'white' },
      PAYMENT_COMPLETED: { backgroundColor: '#6366f1', color: 'white' },
    };

    const statusLabels = {
      APPROVED: 'Approved',
      PAYMENT_PROCESSING: 'Payment Processing',
      PAYMENT_COMPLETED: 'Payment Completed',
    };

    return (
      <span
        style={{
          ...statusStyles[status],
          padding: '4px 12px',
          borderRadius: '9999px',
          fontSize: '12px',
          fontWeight: '500',
        }}
      >
        {statusLabels[status] || status}
      </span>
    );
  };

  const getReconBadge = (reconStatus, onClick) => {
    if (!reconStatus || reconStatus === 'PENDING') return null;

    const config = {
      MATCHED: { icon: '✓', text: 'Recon Matched', bg: '#dcfce7', color: '#166534' },
      MISMATCHED: { icon: '⚠', text: 'Recon Mismatch', bg: '#fef3c7', color: '#92400e' },
      UNREADABLE: { icon: '?', text: 'Unreadable', bg: '#f1f5f9', color: '#64748b' },
    };

    const c = config[reconStatus];
    if (!c) return null;

    return (
      <span
        onClick={onClick}
        style={{
          display: 'inline-block',
          padding: '2px 8px',
          borderRadius: '9999px',
          fontSize: '11px',
          fontWeight: '500',
          backgroundColor: c.bg,
          color: c.color,
          marginLeft: '6px',
          cursor: 'pointer',
        }}
        title="Click to view reconciliation details"
      >
        {c.icon} {c.text}
      </span>
    );
  };

  // Helper to render a payment table row (reused for both local and offshore)
  const renderPaymentRow = (payment, index, showCurrency) => (
    <tr key={payment.id} className={payment.is_paid ? 'row-paid' : ''}>
      <td className="row-number">{index + 1}</td>
      <td className="supplier-name">{payment.supplier_name}</td>
      <td>{payment.account_number}</td>
      <td>{payment.branch_code}</td>
      <td>{payment.reference}</td>
      {showCurrency && (
        <td>
          <span className="breakdown-currency-tag">{payment.currency}</span>
        </td>
      )}
      <td className="amount">{formatAmount(payment.amount)}</td>
      <td className="paid-status">
        {payment.is_paid ? (
          <div>
            <span className="paid-badge">✓ Paid</span>
            <div className="paid-details">
              {payment.paid_by} · {formatDateTime(payment.paid_at)}
            </div>
          </div>
        ) : (
          <span className="unpaid-badge">Pending</span>
        )}
      </td>
      <td className="proof-status">
        {payment.has_proof ? (
          <div>
            <a
              href={payment.proof_url}
              target="_blank"
              rel="noopener noreferrer"
              className="proof-link"
            >
              📎 {payment.proof_filename}
            </a>
            {getReconBadge(payment.recon_status, () => setReconResult({
              supplierName: payment.supplier_name,
              status: payment.recon_status,
              details: payment.recon_details,
            }))}
          </div>
        ) : (
          <span className="no-proof">—</span>
        )}
      </td>
      {(canMarkPaid || canUploadProof) && (
        <td className="actions">
          {canMarkPaid && !payment.is_paid && (
            <button
              className="btn btn-small btn-success"
              onClick={() => handleMarkPaid(payment.id)}
              disabled={markingPaidId === payment.id}
            >
              {markingPaidId === payment.id ? 'Marking...' : 'Mark Paid'}
            </button>
          )}
          {canUploadProof && (
            <>
              <button
                className="btn btn-small btn-secondary"
                onClick={() => handleUploadProofClick(payment.id)}
                disabled={uploadingProofId === payment.id}
              >
                {uploadingProofId === payment.id
                  ? 'Uploading...'
                  : payment.has_proof ? 'Replace POP' : 'Upload POP'}
              </button>
              {payment.has_proof && (
                <button
                  className="btn btn-small btn-danger"
                  onClick={() => handleDeleteProof(payment.id)}
                  disabled={uploadingProofId === payment.id}
                >
                  Remove POP
                </button>
              )}
            </>
          )}
        </td>
      )}
    </tr>
  );

  // Can mark as paid only when batch is in PAYMENT_PROCESSING
  const canMarkPaid = schedule?.batch?.status === 'PAYMENT_PROCESSING';
  
  // Can upload proof when batch is PAYMENT_PROCESSING or PAYMENT_COMPLETED
  const canUploadProof = ['PAYMENT_PROCESSING', 'PAYMENT_COMPLETED'].includes(schedule?.batch?.status);

  if (loading) return <div className="loading">Loading payment schedule...</div>;
  if (error) return (
    <div className="error-container">
      <div className="error">{error}</div>
      <button className="btn btn-secondary" onClick={() => navigate('/batches')}>
        Back to Batches
      </button>
    </div>
  );

  const paidCount = schedule.paid_count || 0;
  const totalCount = schedule.payment_count || 0;
  const progressPercent = totalCount > 0 ? Math.round((paidCount / totalCount) * 100) : 0;

  // Excel column headers (exact match to bank file)
  const excelColumns = [
    'Cr Account Name',
    'Cr Account Number',
    'Cr Branch Number',
    'Cr Statement Ref',
    'Dr Account Name',
    'Dr Account Number',
    'Dr Branch Number',
    'Dr Statement Ref',
    'Payment Date',
    'Amount',
    'Currency',
    'RTGS',
    'Alert Type',
    'Alert Dest',
  ];

  // Button label depends on batch status
  const scheduleButtonLabel = isSendMode
    ? 'Email Schedule to Accountants'
    : 'View Accountant Schedule';

  return (
    <div className="payment-schedule-container">
      {/* Hidden file input for proof uploads */}
      <input
        type="file"
        ref={proofInputRef}
        style={{ display: 'none' }}
        accept=".pdf,.jpg,.jpeg,.png"
        onChange={handleProofFileSelected}
      />

      <div className="schedule-header">
        <div className="header-left">
          <h1>Payment Schedule</h1>
          <p className="batch-reference">
            {schedule.batch.batch_reference}  {getStatusBadge(schedule.batch.status)}
          </p>
        </div>
        <div className="header-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/batches')}
          >
            Back to Batches
          </button>
          {schedule.local_payments.length > 0 && (
            <button 
              className={`btn ${isSendMode ? 'btn-primary' : 'btn-secondary'}`}
              onClick={handlePreviewEmail}
              disabled={loadingRecipients}
            >
              {loadingRecipients ? 'Loading...' : scheduleButtonLabel}
            </button>
          )}
        </div>
      </div>

      {/* Email Success Banner */}
      {emailSuccess && (
        <div className="email-success-banner" style={{
          backgroundColor: '#ecfdf5',
          border: '1px solid #10b981',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <strong style={{ color: '#065f46' }}>✓ {emailSuccess.message}</strong>
            <div style={{ fontSize: '13px', color: '#047857', marginTop: '4px' }}>
              <span>To: {emailSuccess.to.join(', ')}</span>
              {emailSuccess.cc.length > 0 && (
                <span> · CC: {emailSuccess.cc.join(', ')}</span>
              )}
              <span> · File: {emailSuccess.filename}</span>
            </div>
          </div>
          <button 
            onClick={() => setEmailSuccess(null)} 
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: '#065f46' }}
          >
            ✕
          </button>
        </div>
      )}

      <div className="schedule-summary">
        <div className="summary-card">
          <span className="summary-label">Payment Date</span>
          <span className="summary-value">{formatDate(schedule.batch.payment_date)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Payments</span>
          <span className="summary-value">{schedule.payment_count}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Amount</span>
          <span className="summary-value total-amount">{renderTotalAmount()}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Payment Progress</span>
          <span className="summary-value">{paidCount} of {totalCount} paid</span>
          <div className="progress-bar-container">
            <div 
              className="progress-bar-fill" 
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        <div className="summary-card">
          <span className="summary-label">Reviewed By</span>
          <span className="summary-value">{schedule.batch.reviewed_by || '-'}</span>
        </div>
      </div>

      {/* Local Payments Table */}
      {schedule.local_payments.length > 0 && (
        <div className="schedule-table-container">
          <h2 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b' }}>
            Local Payments (ZAR)
            <span style={{ fontWeight: 'normal', color: '#64748b', fontSize: '14px', marginLeft: '8px' }}>
              {schedule.local_payments.length} payment{schedule.local_payments.length !== 1 ? 's' : ''} · Total: {formatAmount(schedule.local_total)}
            </span>
          </h2>
          <table className="schedule-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Supplier</th>
                <th>Account Number</th>
                <th>Branch Code</th>
                <th>Reference</th>
                <th className="amount-column">Amount</th>
                <th>Status</th>
                <th>POP</th>
                {(canMarkPaid || canUploadProof) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {schedule.local_payments.map((payment, index) => 
                renderPaymentRow(payment, index, false)
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Offshore Payments Table */}
      {schedule.offshore_payments.length > 0 && (
        <div className="schedule-table-container" style={{ marginTop: '24px' }}>
          <h2 style={{ margin: '0 0 12px 0', fontSize: '16px', color: '#1e293b' }}>
            Offshore Payments
            <span style={{ fontWeight: 'normal', color: '#64748b', fontSize: '14px', marginLeft: '8px' }}>
              {schedule.offshore_payments.length} payment{schedule.offshore_payments.length !== 1 ? 's' : ''}
              {schedule.offshore_totals.map((t, i) => (
                <span key={i}> · {t.currency}: {formatAmount(t.total)}</span>
              ))}
            </span>
          </h2>
          <table className="schedule-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Supplier</th>
                <th>Account Number</th>
                <th>Branch Code</th>
                <th>Reference</th>
                <th>Currency</th>
                <th className="amount-column">Amount</th>
                <th>Status</th>
                <th>POP</th>
                {(canMarkPaid || canUploadProof) && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {schedule.offshore_payments.map((payment, index) => 
                renderPaymentRow(payment, index, true)
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Reconciliation Results Modal */}
      {reconResult && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '550px' }}>
            <div className="modal-header">
              <h2>
                {reconResult.status === 'MATCHED' ? '✓ ' : reconResult.status === 'MISMATCHED' ? '⚠ ' : ''}
                Reconciliation Result
              </h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b', fontWeight: 'normal' }}>
                {reconResult.supplierName}
              </p>
            </div>
            <div className="modal-body">
              {reconResult.status === 'MATCHED' && (
                <div style={{
                  backgroundColor: '#dcfce7',
                  border: '1px solid #86efac',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#166534',
                  fontWeight: '500',
                }}>
                  All extracted fields match the payment schedule.
                </div>
              )}
              {reconResult.status === 'MISMATCHED' && (
                <div style={{
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#92400e',
                  fontWeight: '500',
                }}>
                  Some fields do not match the payment schedule. Please verify this is the correct proof of payment.
                </div>
              )}
              {reconResult.status === 'UNREADABLE' && (
                <div style={{
                  backgroundColor: '#f1f5f9',
                  border: '1px solid #cbd5e1',
                  borderRadius: '8px',
                  padding: '12px 16px',
                  marginBottom: '16px',
                  color: '#64748b',
                  fontWeight: '500',
                }}>
                  Could not extract payment details from this PDF. Manual verification is required.
                </div>
              )}

              {reconResult.details?.fields && Object.keys(reconResult.details.fields).length > 0 && (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                  <thead>
                    <tr>
                      <th style={reconThStyle}>Field</th>
                      <th style={reconThStyle}>POP Value</th>
                      <th style={reconThStyle}>Schedule Value</th>
                      <th style={{ ...reconThStyle, textAlign: 'center' }}>Match</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(reconResult.details.fields).map(([field, detail]) => (
                      <tr key={field}>
                        <td style={reconTdStyle}>
                          {field === 'account_number' ? 'Account Number' :
                           field === 'reference' ? 'Reference' :
                           field === 'amount' ? 'Amount' : field}
                        </td>
                        <td style={reconTdStyle}>
                          {detail.pop_value || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Not found</span>}
                        </td>
                        <td style={reconTdStyle}>{detail.schedule_value}</td>
                        <td style={{ ...reconTdStyle, textAlign: 'center' }}>
                          {detail.match === true && (
                            <span style={{ color: '#16a34a', fontWeight: '600', fontSize: '16px' }}>✓</span>
                          )}
                          {detail.match === false && (
                            <span style={{ color: '#dc2626', fontWeight: '600', fontSize: '16px' }}>✗</span>
                          )}
                          {detail.match === null && (
                            <span style={{ color: '#94a3b8', fontSize: '16px' }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={() => setReconResult(null)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview & Send Modal */}
      {showPreviewModal && (
        <div className="modal-overlay">
          <div className="modal" style={{ maxWidth: '95vw', width: '1200px' }}>
            <div className="modal-header">
              <h2>{isSendMode ? 'Preview Payment Schedule' : 'Accountant Schedule'}</h2>
              <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#64748b', fontWeight: 'normal' }}>
                {isSendMode
                  ? 'This is exactly what the Excel file will contain. Review before sending.'
                  : 'This is the Excel file that was sent to the accountants.'}
              </p>
            </div>
            <div className="modal-body" style={{ padding: '0', maxHeight: '60vh', overflowY: 'auto' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                }}>
                  <thead>
                    <tr>
                      {excelColumns.map((col, i) => (
                        <th key={i} style={{
                          backgroundColor: '#1e293b',
                          color: 'white',
                          padding: '8px 10px',
                          textAlign: 'left',
                          whiteSpace: 'nowrap',
                          fontSize: '11px',
                          fontWeight: '600',
                          position: 'sticky',
                          top: 0,
                          zIndex: 1,
                        }}>
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.local_payments.map((p, i) => (
                      <tr key={p.id} style={{ backgroundColor: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                        <td style={previewCellStyle}>{p.cr_account_name}</td>
                        <td style={previewCellStyle}>{p.cr_account_number}</td>
                        <td style={previewCellStyle}>{p.cr_branch_number}</td>
                        <td style={previewCellStyle}>{p.cr_statement_ref}</td>
                        <td style={previewCellStyle}>{p.dr_account_name}</td>
                        <td style={previewCellStyle}>{p.dr_account_number}</td>
                        <td style={previewCellStyle}>{p.dr_branch_number}</td>
                        <td style={previewCellStyle}>{p.dr_statement_ref}</td>
                        <td style={previewCellStyle}>{p.payment_date_formatted}</td>
                        <td style={{ ...previewCellStyle, textAlign: 'right' }}>{formatAmount(p.amount)}</td>
                        <td style={previewCellStyle}>{p.currency}</td>
                        <td style={previewCellStyle}>{p.rtgs}</td>
                        <td style={previewCellStyle}>{p.alert_type}</td>
                        <td style={previewCellStyle}>{p.alert_dest}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Summary and Recipients */}
              <div style={{ padding: '16px 20px', borderTop: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', gap: '32px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>
                      File Summary
                    </div>
                    <div style={{ fontSize: '14px', color: '#1e293b' }}>
                      {schedule.local_payments.length} payment{schedule.local_payments.length !== 1 ? 's' : ''} · ZAR {formatAmount(schedule.local_total)}
                    </div>
                  </div>

                  {isSendMode && (
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>
                        To
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e293b' }}>
                        {recipients.to.map(r => `${r.name} <${r.email}>`).join(', ')}
                      </div>
                    </div>
                  )}

                  {isSendMode && recipients.cc.length > 0 && (
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748b', fontWeight: '600', marginBottom: '4px', textTransform: 'uppercase' }}>
                        CC
                      </div>
                      <div style={{ fontSize: '14px', color: '#1e293b' }}>
                        {recipients.cc.map(r => `${r.name} <${r.email}>`).join(', ')}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowPreviewModal(false)}
                disabled={emailing}
              >
                {isSendMode ? 'Cancel' : 'Close'}
              </button>
              {isSendMode && (
                <button 
                  className="btn btn-primary" 
                  onClick={handleConfirmSend}
                  disabled={emailing}
                >
                  {emailing ? 'Sending...' : 'Confirm & Send'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Processing Status Modal */}
      {showProcessingModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>Update Batch Status</h2>
            </div>
            <div className="modal-body">
              <p>The payment schedule has been emailed to the accountants.</p>
              <p>Would you like to change the batch status to <strong>Payment Processing</strong>?</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowProcessingModal(false)}
                disabled={updatingStatus}
              >
                Not Yet
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleStartProcessing}
                disabled={updatingStatus}
              >
                {updatingStatus ? 'Updating...' : 'Yes, Start Processing'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Batch Completed Modal */}
      {showCompletedModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>🎉 Batch Complete</h2>
            </div>
            <div className="modal-body">
              <p>All payments have been marked as paid.</p>
              <p>The batch status has been automatically updated to <strong>Payment Completed</strong>.</p>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-primary" 
                onClick={() => setShowCompletedModal(false)}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        confirmStyle={confirmModal.confirmStyle}
        loading={confirmModal.loading}
        cancelText="Cancel"
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ show: false })}
      />
    </div>
  );
};

// Style constants
const previewCellStyle = {
  padding: '6px 10px',
  borderBottom: '1px solid #e2e8f0',
  whiteSpace: 'nowrap',
};

const reconThStyle = {
  padding: '8px 12px',
  textAlign: 'left',
  fontSize: '12px',
  fontWeight: '600',
  color: '#64748b',
  borderBottom: '2px solid #e2e8f0',
  textTransform: 'uppercase',
};

const reconTdStyle = {
  padding: '10px 12px',
  borderBottom: '1px solid #f1f5f9',
  fontSize: '14px',
  color: '#1e293b',
};

export default PaymentSchedule;